<template>
  <div>
    <popup-add-product
      ref="popupAddProduct"
      @productSelected="addProduct"
      :existingProducts="products"
      :isReceiving="isReceiving"
      :activeVenueId="activeVenueId"
      :otherVenueId="otherVenueId"
    />

    <vs-sidebar
      parent="body"
      click-not-close
      position-right
      default-index="1"
      color="primary"
      class="add-new-data-sidebar items-no-padding"
      spacer
      v-model="isActive"
    >
      <div class="mt-6 flex items-center justify-between px-6">
        <div class="flex items-center">
          <img width="40" :src="ProductIcon" class="mr-4" />
          <div>
            <h4>Stock transfer request</h4>
          </div>
        </div>
        <feather-icon
          icon="XIcon"
          @click.stop="isActive = false"
          class="cursor-pointer"
        ></feather-icon>
      </div>
      <vs-divider class="mb-0"></vs-divider>

      <component
        :is="scrollbarTag"
        class="scroll-area--data-list-add-new"
        :settings="settings"
        :key="$vs.rtl"
      >
        <Loading v-if="isLoading" />
        <div v-else class="p-6">
          <div class="flex flex-col mb-3" v-if="isNewTransfer">
            <div class="mb-1 text-sm">
              <span>Venue</span>
            </div>
            <div v-if="isNewTransfer">
              <VenueSelector
                v-model="otherVenueId"
                v-on:venue="onSelectVenue"
                :filter-regions="[activeVenue.regionId]"
                :global-mode="true"
                :deactivated="products.length > 0"
              />
            </div>
          </div>
          <div class="flex flex-col mb-3" v-if="!isNewTransfer">
            <div class="mb-1 text-sm">
              <span>State</span>
            </div>
            <div>
              <vs-chip class="whitespace-no-wrap" :color="getStateColor(state)">
                {{ getStateLabel(state) }}
              </vs-chip>
            </div>
          </div>

          <div class="flex flex-col mb-3" v-if="!isNewTransfer">
            <div class="mb-1 text-sm">
              <span>Requested by</span>
            </div>
            <div>
              <code
                >{{ createdBy.username }} at {{ createdAt | formatDate }}</code
              >
            </div>
          </div>

          <div class="flex flex-col mb-3" v-if="!isNewTransfer && processedBy">
            <div class="mb-1 text-sm">
              <span>Processed by</span>
            </div>
            <div>
              <code
                >{{ processedBy.username }} at
                {{ processedAt | formatDate }}</code
              >
            </div>
          </div>

          <div class="flex flex-col mb-4" v-if="otherVenueId">
            <div class="text-sm">
              <span>Request stock transfer from and to</span>
            </div>
            <div>
              <div class="flex items-center">
                <vx-tooltip :text="transferText" position="bottom">
                  <code class="flex items-center mt-1 mb-2 text-sm">
                    <div>
                      {{
                        isReceiving ? otherVenue.venueName : activeVenue.name
                      }}
                    </div>
                    <div>
                      <feather-icon
                        icon="ArrowRightIcon"
                        class="mx-2 mt-1"
                        svgClasses="h-5 w-5 stroke-2"
                      />
                    </div>
                    <div>
                      {{
                        isReceiving ? activeVenue.name : otherVenue.venueName
                      }}
                    </div>
                  </code>
                </vx-tooltip>
                <vx-tooltip
                  v-if="isNewTransfer && this.products.length == 0"
                  :text="`Click to switch between receiving and sending venue`"
                  position="bottom"
                  class="inline-block"
                >
                  <feather-icon
                    class="cursor-pointer ml-2"
                    icon="RefreshCwIcon"
                    svgClasses="w-4 h-4"
                    @click="isReceiving = !isReceiving"
                  />
                </vx-tooltip>
              </div>
            </div>
          </div>

          <div class="mt-4" v-if="otherVenueId">
            <!-- <div class="mb-1 text-sm">
            <span>Request products</span>
          </div> -->

            <vs-table
              :data="products"
              style="margin-left: -24px; margin-right: -24px"
              noDataText=""
            >
              <template slot="header">
                <h3 class="ml-6 mb-1">Products</h3>
              </template>
              <template slot="thead">
                <vs-th sort-key="name"> Name </vs-th>
                <vs-th sort-key="amount"> Amount </vs-th>
                <vs-th></vs-th>
              </template>

              <template slot-scope="{ data }">
                <vs-tr
                  :data="tr"
                  :key="tr.id"
                  v-for="tr in data"
                  :state="tr.amount == 0 ? 'danger' : ''"
                >
                  <vs-td>
                    <div>{{ tr.name }}</div>
                    <div>
                      <small class="text-grey">{{ tr.id }}</small>
                    </div>
                  </vs-td>

                  <vs-td>
                    <div
                      v-if="isEditable && isNewTransfer"
                      class="flex items-center"
                    >
                      <vs-input
                        v-model="tr.amount"
                        type="number"
                        :min="0"
                        :max="tr.maxAmount"
                        style="max-width: 4.5rem"
                      />
                      <div v-if="tr.maxAmount" class="pre-wrap ml-1">
                        /&nbsp;{{ tr.maxAmount }}
                      </div>
                    </div>
                    <div
                      v-else-if="isEditable && !isNewTransfer"
                      class="flex items-center"
                    >
                      <vs-input
                        v-model="tr.amount"
                        type="number"
                        :min="0"
                        :max="tr.originalAmount"
                        style="max-width: 4.5rem"
                      />
                      <vx-tooltip
                        text="The originally requested amount"
                        position="bottom"
                      >
                        <div v-if="tr.originalAmount" class="pre-wrap ml-1">
                          ({{ tr.originalAmount }})
                        </div>
                      </vx-tooltip>
                    </div>
                    <div v-else>
                      <span class="text-muted"> {{ tr.amount }}</span>
                    </div>
                  </vs-td>

                  <vs-td>
                    <feather-icon
                      v-if="isNewTransfer"
                      @click="removeProduct(tr.id)"
                      icon="TrashIcon"
                      class="cursor-pointer mr-2"
                      svgClasses="h-5 w-5 stroke-2"
                    />
                    <div v-else-if="isEditable">
                      <feather-icon
                        @click="deselectProductForAccept(tr.id)"
                        icon="XCircleIcon"
                        class="cursor-pointer mr-2"
                        svgClasses="h-5 w-5 stroke-2"
                      />
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>

            <div class="flex justify-center mt-2" v-if="isNewTransfer">
              <vs-button
                class="ml-2 py-2 px-3 rounded"
                @click="$refs.popupAddProduct.show()"
              >
                Add product
              </vs-button>
            </div>
          </div>

          <!-- <vs-divider v-if="!isNewTransfer" /> -->
          <div class="my-5"></div>

          <div class="flex flex-col mb-3" v-if="!isNewTransfer && isEditable">
            <div class="mb-1 text-sm">
              <span>Action</span>
            </div>
            <div>
              <vs-select v-model="action" class="w-full">
                <vs-select-item
                  :key="index"
                  :value="item.type"
                  :text="item.description"
                  v-for="(item, index) in actionChoices"
                />
              </vs-select>
            </div>
          </div>

          <div
            class="flex flex-col mt-3"
            v-if="!isNewTransfer && isEditable && actionHasComment"
          >
            <div class="mb-1 text-sm">
              <span>Comment</span>
            </div>
            <div>
              <vs-textarea class="w-full" v-model="comment" />
            </div>
          </div>

          <div
            class="flex flex-col mb-3 mt-2"
            v-if="!isNewTransfer && !isEditable"
          >
            <div class="mb-1 text-sm">
              <span>Comment</span>
            </div>
            <div>
              <span v-if="comment">{{ comment }} </span>
              <i v-else>No comment</i>
            </div>
          </div>
        </div>
      </component>

      <div class="flex flex-wrap items-center p-6" slot="footer">
        <feather-icon
          v-if="!isFormValid"
          icon="AlertTriangleIcon"
          svgClasses="text-danger"
        ></feather-icon>
        <span v-if="!isFormValid" class="mb-2 color-red"
          >A comment must be provided</span
        >
        <vs-button
          class="mr-6"
          @click="submitData"
          :disabled="!isFormValid"
          v-if="isNewTransfer"
        >
          Create
        </vs-button>
        <vs-button
          class="mr-6"
          @click="performAction"
          :disabled="!isFormValid"
          v-if="!isNewTransfer && isEditable"
        >
          Submit
        </vs-button>
        <vs-button type="border" color="danger" @click="isActive = false"
          >Cancel</vs-button
        >
      </div>
    </vs-sidebar>
  </div>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import ProductIcon from "@/assets/images/product-icon.png";
import { mapGetters } from "vuex";
import VenueSelector from "@/components/VenueSelector.vue";
import PopupAddProduct from "./PopupAddProduct.vue";

export default {
  components: {
    VenueSelector,
    VuePerfectScrollbar,
    PopupAddProduct,
  },
  data() {
    return {
      settings: {
        // perfectscrollbar settings
        maxScrollbarLength: 60,
        wheelSpeed: 0.6,
      },
      ProductIcon,

      isActive: false,
      isLoading: true,
      isNewTransfer: false,

      action: null, // "accept", "reject", "partialAccept"

      // Specific model fields
      stockTransferId: null,
      initiatingVenueId: null,
      otherVenueId: null,
      otherVenue: null,
      isReceiving: true,
      comment: "",
      products: [],

      processedBy: null,
      processedAt: null,
      createdBy: null,
      createdAt: null,
      state: null,
    };
  },
  watch: {
    products: {
      handler: function (val) {
        if (this.isEditable) this.action = null;
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters({
      vendors: "vendors/vendors",
      activeVenue: "venues/activeVenue",
      activeVenueId: "venues/activeVenueId",
      stateMapping: "stockTransfers/stateMapping",
    }),
    isFormValid() {
      if (this.action === "partialAccept" || this.action === "decline") {
        if (!this.comment || this.comment.trim().length <= 0) return false;
      }
      if (!this.otherVenueId) return false;
      if (this.products.length <= 0) return false;
      return true;
    },

    actionChoices() {
      let result = [{ type: "decline", description: "Decline" }];
      if (
        this.isEditable &&
        !this.isNewTransfer &&
        this.products.some((e) => e.amount != e.originalAmount)
      )
        result.push({ type: "partialAccept", description: "Partial Accept" });
      else result.push({ type: "accept", description: "Accept" });
      return result;
    },

    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    },
    isEditable() {
      return (
        this.state !== "Accepted" &&
        this.state !== "Declined" &&
        this.state !== "PartiallyAccepted" &&
        this.activeVenueId !== this.initiatingVenueId
      );
    },
    actionHasComment() {
      return this.action === "decline" || this.action === "partialAccept";
    },

    transferText() {
      return `${
        this.isReceiving ? this.otherVenue.venueName : this.activeVenue.name
      } will send stock to ${
        this.isReceiving ? this.activeVenue.name : this.otherVenue.venueName
      }`;
    },
  },
  methods: {
    async showNewRequest() {
      this.isNewTransfer = true;

      this.stockTransferId = null;
      this.otherVenueId = null;
      this.otherVenue = null;
      this.isReceiving = true;
      this.comment = "";
      this.products = [];

      this.processedBy = null;
      this.processedAt = null;
      this.createdBy = null;
      this.createdAt = null;
      this.state = null;

      this.isActive = true;
      this.isLoading = false;
    },

    async show(stockTransferId) {
      this.isNewTransfer = false;
      var result = await this.$store.dispatch(
        "stockTransfers/getStockTransfer",
        stockTransferId
      );

      if (this.activeVenueId == result.fromVenueId.id) {
        this.isReceiving = false;
        this.otherVenueId = result.toVenueId.id;
        this.otherVenue = { venueName: result.toVenueName };
      } else {
        this.isReceiving = true;
        this.otherVenueId = result.fromVenueId.id;
        this.otherVenue = { venueName: result.fromVenueName };
      }
      this.stockTransferId = stockTransferId;
      this.comment = result.comment;
      this.processedBy = result.processedBy;
      this.processedAt = result.processedAt;
      this.createdBy = result.createdBy;
      this.createdAt = result.createdAt;
      this.state = result.state;
      this.products = result.stockTransferAmounts.map((item) => ({
        id: item.stockProductId.id,
        name: item.productName,
        amount: item.amount,
        originalAmount: item.amount, // The amount that was originally requested
      }));
      this.initiatingVenueId =
        result.initiatingVenueId != null ? result.initiatingVenueId.id : "";

      console.log("RESULT", result);

      this.isActive = true;
      this.isLoading = false;
    },

    async onSelectVenue(venue) {
      this.otherVenue = venue;
    },

    getStateLabel(state) {
      // Get first state that matches in array stateFilters and return the label of that
      const match = this.stateMapping.find((filter) => filter.value === state);
      return match ? match.label : state;
    },
    getStateColor(state) {
      // Get first state that matches in array stateFilters and return the color of that
      const match = this.stateMapping.find((filter) => filter.value === state);
      return match ? match.color : state;
    },

    addProduct(product) {
      console.log("PRODUCT ADD", product);
      this.products.push({
        id: product.id,
        name: product.name,
        amount: 1,
        maxAmount: product.maxAmount,
      });
    },

    removeProduct(id) {
      this.products = this.products.filter((product) => product.id !== id);
    },

    deselectProductForAccept(id) {
      this.products = this.products.map((product) => {
        if (product.id === id) {
          product.amount = 0;
        }
        return product;
      });
    },

    async performAction() {
      try {
        switch (this.action) {
          case "accept":
            await this.$store.dispatch("stockTransfers/acceptStockTransfer", {
              stockTransferId: this.stockTransferId,
            });
            break;
          case "decline":
            await this.$store.dispatch("stockTransfers/declineStockTransfer", {
              stockTransferId: this.stockTransferId,
              dto: { comment: this.comment },
            });
            break;
          case "partialAccept":
            await this.$store.dispatch(
              "stockTransfers/partiallyAcceptStockTransfer",
              {
                stockTransferId: this.stockTransferId,
                dto: {
                  comment: this.comment,
                  stockTransferAmountRequests: this.products.map((item) => ({
                    stockProductId: item.id,
                    amount: item.amount,
                  })),
                },
              }
            );
            break;
          default:
            throw new Error("Unknown submit action");
        }

        this.$vs.notify({
          title: "Success",
          text: "Stock transfer was successfully updated",
          color: "green",
        });

        await this.$store.dispatch(
          "stockTransfers/getStockTransfers",
          this.activeVenueId
        );
        this.isActive = false;
      } catch (err) {
        let message = err;
        if (err.error) message = err.error;

        this.$vs.notify({
          title: "Could not submit",
          text: message,
          color: "danger",
          time: 5000,
        });
      }
    },

    async submitData() {
      try {
        await this.$store.dispatch("stockTransfers/createStockTransfer", {
          initiatingVenueId: this.activeVenueId,
          fromVenueId: this.isReceiving
            ? this.otherVenueId
            : this.activeVenueId,
          toVenueId: this.isReceiving ? this.activeVenueId : this.otherVenueId,
          stockTransferAmountRequests: this.products.map((product) => ({
            stockProductId: product.id,
            amount: product.amount,
          })),
        });

        this.$vs.notify({
          title: "Success",
          text: "Stock transfer was successfully created",
          color: "green",
        });

        await this.$store.dispatch(
          "stockTransfers/getStockTransfers",
          this.activeVenueId
        );
        this.isActive = false;
      } catch (err) {
        let message = err;
        if (err.error) message = err.error;

        this.$vs.notify({
          title: "Could not load stock transfers",
          text: message,
          color: "danger",
          time: 5000,
        });
      } finally {
        this.isLoading = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add-new-data-sidebar {
  ::v-deep .vs-sidebar--background {
    z-index: 52010;
  }

  ::v-deep .vs-sidebar {
    z-index: 52010;
    width: 400px;
    max-width: 90vw;

    .img-upload {
      margin-top: 2rem;

      .con-img-upload {
        padding: 0;
      }

      .con-input-upload {
        width: 100%;
        margin: 0;
      }
    }
  }
}

.scroll-area--data-list-add-new {
  // height: calc(var(--vh, 1vh) * 100 - 4.3rem);
  height: calc(var(--vh, 1vh) * 100 - 16px - 45px - 82px);

  &:not(.ps) {
    overflow-y: auto;
  }
}
</style>